import type { WebsiteMediaType } from '../graphql/types';

/** 与后端 WebsiteMediaFormat 一致：按扩展名定类型与大小上限，SVG 不支持 */
interface MediaFormatRule {
    mediaType: WebsiteMediaType;
    maxBytes: number;
}

const MB = 1024 * 1024;

const FORMAT_RULES: Record<string, MediaFormatRule> = {
    jpg: { mediaType: 'IMAGE', maxBytes: 10 * MB },
    jpeg: { mediaType: 'IMAGE', maxBytes: 10 * MB },
    png: { mediaType: 'IMAGE', maxBytes: 10 * MB },
    webp: { mediaType: 'IMAGE', maxBytes: 10 * MB },
    gif: { mediaType: 'IMAGE', maxBytes: 10 * MB },
    mp4: { mediaType: 'VIDEO', maxBytes: 1024 * MB },
    pdf: { mediaType: 'DOCUMENT', maxBytes: 50 * MB },
    doc: { mediaType: 'DOCUMENT', maxBytes: 50 * MB },
    docx: { mediaType: 'DOCUMENT', maxBytes: 50 * MB },
    xls: { mediaType: 'DOCUMENT', maxBytes: 50 * MB },
    xlsx: { mediaType: 'DOCUMENT', maxBytes: 50 * MB },
    ppt: { mediaType: 'DOCUMENT', maxBytes: 50 * MB },
    pptx: { mediaType: 'DOCUMENT', maxBytes: 50 * MB },
};

/** 文件选择框的 accept，按媒体类型 */
export const MEDIA_ACCEPT: Record<WebsiteMediaType, string> = {
    IMAGE: '.jpg,.jpeg,.png,.webp,.gif',
    VIDEO: '.mp4',
    DOCUMENT: '.pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx',
};

/** 后端文件名上限 255 字符 */
export const MAX_FILE_NAME_LENGTH = 255;

export const fileExtension = (fileName: string): string => {
    const dot = fileName.lastIndexOf('.');
    return dot > 0 && dot < fileName.length - 1 ? fileName.slice(dot + 1).toLowerCase() : '';
};

export const formatRuleOf = (fileName: string): MediaFormatRule | null => FORMAT_RULES[fileExtension(fileName)] ?? null;

export type MediaCheckProblem = 'UNSUPPORTED_TYPE' | 'WRONG_TYPE' | 'TOO_LARGE' | 'EMPTY' | 'NAME_TOO_LONG';

export interface MediaCheckResult {
    problem: MediaCheckProblem | null;
    rule: MediaFormatRule | null;
}

/** 前端预检：类型、大小、文件名长度，与服务端规则一致，服务端仍会再校验 */
export const checkMediaFile = (file: File, expected?: WebsiteMediaType): MediaCheckResult => {
    const rule = formatRuleOf(file.name);
    if (!rule) return { problem: 'UNSUPPORTED_TYPE', rule };
    if (expected && rule.mediaType !== expected) return { problem: 'WRONG_TYPE', rule };
    if (file.size <= 0) return { problem: 'EMPTY', rule };
    if (file.size > rule.maxBytes) return { problem: 'TOO_LARGE', rule };
    if ([...file.name].length > MAX_FILE_NAME_LENGTH) return { problem: 'NAME_TOO_LONG', rule };
    return { problem: null, rule };
};
